import type { SessionService } from '@efdigital/ll-addon-auth';
import {
  COACH_PICKLIST_GROUPS,
  picklistLabel,
} from '@efdigital/ll-addon-coaching/utils/picklist-labels';
import type PicklistsService from '@efdigital/ll-addon-content/services/picklists';
import type { CoachingPartnershipMessage } from '@efdigital/ll-addon-data';
import { DataTypeCoaching } from '@efdigital/ll-addon-data/data-types';
import MessageComposer from '@efdigital/ll-xceed-v2/components/coaching/messaging/message-composer.gts';
import ParticipantAvatar from '@efdigital/ll-xceed-v2/components/coaching/messaging/participant-avatar.gts';
import { asAsyncBelongsTo } from '@efdigital/ll-xceed-v2/data/async-setter';
import {
  createPartnershipMessage,
  queryPartnershipMessages,
} from '@efdigital/ll-xceed-v2/data/builders/coaching/partnership-messages';
import {
  formatMessageTimestamp,
  type MessagingRole,
  type PartnershipConversation,
  sortMessages,
} from '@efdigital/ll-xceed-v2/utils/coaching/partnership-messages';
import { array } from '@ember/helper';
import { on } from '@ember/modifier';
import { LinkTo } from '@ember/routing';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { cached, tracked } from '@glimmer/tracking';
import type { Store } from '@warp-drive/core';
import { Request } from '@warp-drive/ember';
import { resource, use } from 'ember-resources';

/**
 * How often the open thread refetches. `<Request>` skips the interval while the
 * tab is hidden, so a backgrounded conversation costs nothing.
 */
const REFRESH_INTERVAL_MS = 15_000;

interface MessageConversationSignature {
  Args: {
    conversation: PartnershipConversation;
    role: MessagingRole;
    programId?: string;
    cohortId?: string;
  };
  Element: HTMLDivElement;
}

function eq(a: unknown, b: unknown): boolean {
  return a === b;
}

function showTimestamp(
  index: number,
  messages: CoachingPartnershipMessage[]
): boolean {
  const current = messages[index];
  const next = messages[index + 1];

  if (!current) {
    return false;
  }

  return !next || next.senderUserId !== current.senderUserId;
}

function showAvatar(
  index: number,
  messages: CoachingPartnershipMessage[],
  senderUserId: string
): boolean {
  if (messages[index]?.senderUserId !== senderUserId) {
    return false;
  }

  const next = messages[index + 1];
  return !next || next.senderUserId !== senderUserId;
}

interface ConversationMessagesSyncSignature {
  Args: {
    conversation: PartnershipConversation;
    messages: CoachingPartnershipMessage[];
  };
}

/**
 * Keeps the inbox model in sync with the open thread's polling result.
 * `messages` is tracked on `PartnershipConversation`, so its preview, date, and
 * unread state update when this resource receives a newer result.
 */
class ConversationMessagesSync extends Component<ConversationMessagesSyncSignature> {
  @use sync = resource(() => {
    const messages = this.args.messages;
    const currentIds = this.args.conversation.messages.map(({ id }) => id);
    const nextIds = messages.map(({ id }) => id);

    if (
      currentIds.length !== nextIds.length ||
      currentIds.some((id, index) => id !== nextIds[index])
    ) {
      this.args.conversation.messages = messages;
    }

    return false;
  });

  <template></template>
}

export default class MessageConversation extends Component<MessageConversationSignature> {
  @service declare session: SessionService;
  @service declare store: Store;
  @service declare picklists: PicklistsService;

  /** Messages sent from here that the latest fetch has not caught up with yet. */
  @tracked sentMessages: CoachingPartnershipMessage[] = [];
  @tracked sentMessagesPartnershipId: string | null = null;
  @tracked isSending = false;
  @tracked errorMessage: string | null = null;

  get partnershipId(): string {
    return this.args.conversation.partnership.id!;
  }

  /** Sends and errors belong to the thread they happened in, not the component. */
  get sendStateIsCurrent(): boolean {
    return this.sentMessagesPartnershipId === this.partnershipId;
  }

  @cached
  get messagesQuery() {
    return queryPartnershipMessages(this.partnershipId);
  }

  get composerError(): string | null {
    return this.sendStateIsCurrent ? this.errorMessage : null;
  }

  get composerSending(): boolean {
    return this.sendStateIsCurrent ? this.isSending : false;
  }

  get currentUserId(): string {
    return this.session.cached.userId!;
  }

  get participantSecondary(): string {
    const participant = this.args.conversation.participant;

    if (this.args.role === 'coach') {
      return participant.secondary;
    }

    const coachingTypeKey = participant.coach?.coachingTypes.at(0);

    if (!coachingTypeKey) {
      return 'Coach';
    }

    return picklistLabel(
      this.picklists,
      COACH_PICKLIST_GROUPS.coachingTypes,
      coachingTypeKey
    );
  }

  /**
   * Keeps a just-sent message on screen until a refresh returns it, without
   * showing it twice once that refresh lands.
   */
  threadMessages = (
    fetched: readonly CoachingPartnershipMessage[]
  ): CoachingPartnershipMessage[] => {
    const messages = sortMessages(fetched);

    if (!this.sendStateIsCurrent) {
      return messages;
    }

    const pending = this.sentMessages.filter(
      (sent) => !messages.some((message) => message.id === sent.id)
    );

    return pending.length ? sortMessages([...messages, ...pending]) : messages;
  };

  sendMessage = async (body: string) => {
    const conversation = this.args.conversation;

    if (!this.sendStateIsCurrent) {
      this.sentMessages = [];
      this.sentMessagesPartnershipId = this.partnershipId;
    }

    this.isSending = true;
    this.errorMessage = null;

    const created = this.store.createRecord<CoachingPartnershipMessage>(
      DataTypeCoaching.CoachingPartnershipMessage,
      {
        coachingPartnership: asAsyncBelongsTo(conversation.partnership),
        user: this.currentUserId,
        body,
      }
    );

    try {
      await this.store.request(createPartnershipMessage(created, this.store));
      this.sentMessages = [...this.sentMessages, created];
      // The inbox preview reads the model, which the thread's own fetch skips.
      conversation.messages = [...conversation.messages, created];
    } catch (error) {
      created.unloadRecord();
      console.error(error);
      this.errorMessage = 'Your message could not be sent. Please try again.';
      throw error;
    } finally {
      this.isSending = false;
    }
  };

  <template>
    <div
      class="d-flex flex-column h-100 w-100 min-h-0 coaching-messages-conversation"
      data-test-message-conversation
      ...attributes
    >
      <div
        class="card border shadow-sm rounded-4 d-flex flex-column min-h-0 overflow-hidden coaching-messages-conversation-box"
      >
        <div
          class="d-flex align-items-center justify-content-between gap-2 px-3 py-2 border-bottom bg-body flex-shrink-0 coaching-messages-conversation-header"
        >
          <div class="d-flex align-items-center gap-2 min-w-0">
            {{#if (eq @role "coach")}}
              <LinkTo
                @route="coaching.coach.messages.index"
                class="btn btn-outline-primary border-0 rounded-circle p-2 d-lg-none d-inline-flex align-items-center justify-content-center flex-shrink-0 coaching-messages-back-btn"
                aria-label="Back to messages"
              >
                <iconify-icon
                  inline
                  icon="ph:arrow-left"
                  class="fs-5"
                  aria-hidden="true"
                />
              </LinkTo>
              <LinkTo
                @route="coaching.coach.coachees.coachee"
                @models={{array @conversation.partnership.user}}
                class="d-flex align-items-center gap-2 min-w-0 text-decoration-none text-body"
              >
                <ParticipantAvatar
                  @participant={{@conversation.participant}}
                  @size="xs"
                />
                <div class="min-w-0">
                  <h1
                    class="h6 fw-bold mb-0 text-truncate"
                  >{{@conversation.participant.name}}</h1>
                  <p class="small text-body-secondary mb-0 text-truncate">
                    {{this.participantSecondary}}
                  </p>
                </div>
              </LinkTo>
            {{else}}
              <LinkTo
                @route="coaching.coachee.cohort.messages.index"
                @models={{array @programId @cohortId}}
                class="btn btn-outline-primary border-0 rounded-circle p-2 d-lg-none d-inline-flex align-items-center justify-content-center flex-shrink-0 coaching-messages-back-btn"
                aria-label="Back to messages"
              >
                <iconify-icon
                  inline
                  icon="ph:arrow-left"
                  class="fs-5"
                  aria-hidden="true"
                />
              </LinkTo>
              <LinkTo
                @route="coaching.coachee.cohort.coaches.coach"
                @models={{array
                  @programId
                  @cohortId
                  @conversation.partnership.coachUserId
                }}
                class="d-flex align-items-center gap-2 min-w-0 text-decoration-none text-body"
              >
                <ParticipantAvatar
                  @participant={{@conversation.participant}}
                  @size="xs"
                />
                <div class="min-w-0">
                  <h1
                    class="h6 fw-bold mb-0 text-truncate"
                  >{{@conversation.participant.name}}</h1>
                  <p class="small text-body-secondary mb-0 text-truncate">
                    {{this.participantSecondary}}
                  </p>
                </div>
              </LinkTo>
            {{/if}}
          </div>

          <div class="d-flex align-items-center gap-1 flex-shrink-0">
            {{#if (eq @role "coach")}}
              <LinkTo
                @route="coaching.coach.sessions"
                class="btn btn-outline-primary border-0 rounded-circle p-0 d-inline-flex align-items-center justify-content-center ll-avatar-xs"
                aria-label="View sessions"
              >
                <iconify-icon
                  inline
                  icon="ph:calendar-blank"
                  class="fs-5"
                  aria-hidden="true"
                />
              </LinkTo>
            {{else}}
              <LinkTo
                @route="coaching.coachee.cohort.sessions"
                @models={{array @programId @cohortId}}
                class="btn btn-outline-primary border-0 rounded-circle p-0 d-inline-flex align-items-center justify-content-center ll-avatar-xs"
                aria-label="Schedule session"
              >
                <iconify-icon
                  inline
                  icon="ph:calendar-blank"
                  class="fs-5"
                  aria-hidden="true"
                />
              </LinkTo>
              <LinkTo
                @route="coaching.coachee.cohort.coaches.coach"
                @models={{array
                  @programId
                  @cohortId
                  @conversation.partnership.coachUserId
                }}
                class="btn btn-outline-primary border-0 rounded-circle p-0 d-inline-flex align-items-center justify-content-center ll-avatar-xs"
                aria-label="View coach profile"
              >
                <iconify-icon
                  inline
                  icon="ph:info"
                  class="fs-5"
                  aria-hidden="true"
                />
              </LinkTo>
            {{/if}}
          </div>
        </div>

        <div
          class="flex-grow-1 overflow-auto min-h-0 px-3 py-3 bg-body-secondary coaching-messages-thread-pane"
        >
          <div
            class="d-flex flex-column gap-1 mx-auto coaching-messages-thread"
          >
            <Request
              @query={{this.messagesQuery}}
              @autorefresh="interval,online"
              @autorefreshThreshold={{REFRESH_INTERVAL_MS}}
              @autorefreshBehavior="refresh"
            >
              <:content as |result|>
                {{#let (this.threadMessages result.data) as |messages|}}
                  <ConversationMessagesSync
                    @conversation={{@conversation}}
                    @messages={{messages}}
                  />
                  {{#each messages as |message index|}}
                    {{#if (eq message.senderUserId this.currentUserId)}}
                      <div class="d-flex flex-column align-items-end">
                        <div
                          class="text-bg-primary px-3 py-2 coaching-messages-bubble coaching-messages-bubble-sent"
                        >
                          <p class="mb-0 lh-base small">{{message.body}}</p>
                        </div>
                        {{#if (showTimestamp index messages)}}
                          <span
                            class="me-1 mt-1 small text-body-secondary"
                          >{{formatMessageTimestamp message.createdAt}}</span>
                        {{/if}}
                      </div>
                    {{else}}
                      <div class="d-flex align-items-end gap-2">
                        {{#if (showAvatar index messages message.senderUserId)}}
                          <span
                            class="ll-avatar-xxs rounded-circle text-bg-primary d-inline-flex align-items-center justify-content-center fw-bold flex-shrink-0 mb-1"
                            aria-hidden="true"
                          >{{@conversation.participant.initials}}</span>
                        {{else}}
                          <span
                            class="ll-avatar-xxs flex-shrink-0"
                            aria-hidden="true"
                          ></span>
                        {{/if}}
                        <div class="d-flex flex-column align-items-start">
                          <div
                            class="bg-body px-3 py-2 coaching-messages-bubble coaching-messages-bubble-received shadow-sm"
                          >
                            <p
                              class="mb-0 text-body lh-base small"
                            >{{message.body}}</p>
                          </div>
                          {{#if (showTimestamp index messages)}}
                            <span
                              class="ms-1 mt-1 small text-body-secondary"
                            >{{formatMessageTimestamp message.createdAt}}</span>
                          {{/if}}
                        </div>
                      </div>
                    {{/if}}
                  {{else}}
                    <p class="small text-body-secondary text-center mb-0">
                      No messages yet. Say hello to start the conversation.
                    </p>
                  {{/each}}
                {{/let}}
              </:content>

              <:loading>
                <p
                  class="small text-body-secondary text-center mb-0"
                  role="status"
                  aria-live="polite"
                  data-test-messages-loading
                >
                  Loading messages…
                </p>
              </:loading>

              <:error as |_error state|>
                <div class="text-center" role="alert">
                  <p class="small text-body-secondary mb-2">
                    Messages could not be loaded.
                  </p>
                  <button
                    type="button"
                    class="btn btn-sm btn-outline-primary"
                    {{on "click" state.retry}}
                  >
                    Try again
                  </button>
                </div>
              </:error>
            </Request>
          </div>
        </div>

        <MessageComposer
          @recipientFirstName={{@conversation.participant.firstName}}
          @isSending={{this.composerSending}}
          @errorMessage={{this.composerError}}
          @onSend={{this.sendMessage}}
          class="flex-shrink-0 coaching-messages-composer"
        />
      </div>
    </div>
  </template>
}
